/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs, { extend } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import * as yup from "yup";

import { OrderFormData, OrderTabs } from "@/components/screens/OrderFormPage/types";
import { convertCurrencyToNumber } from "@/helpers/currency-helpers";
import { getISOCurrentDateTime } from "@/helpers/date-helpers";
import { normalizeEmptyStrings } from "@/helpers/string-helpers";
import { IOrder, IOrderInput, IOrderItem, IOrderPayment } from "@/interfaces/order.interfaces";
import IUser from "@/interfaces/user.interface";
import { OrderFormPageMessages } from "@/messages/order-form-page/order-form-page.messages";
import { DocumentType, PersonType } from "@/types/document.types";
import { OrderSalesChannel, OrderStatus, PaymentStatus } from "@/types/order.types";
import { PaymentMethod } from "@/types/payment.types";
import { UserProfile } from "@/types/user.types";

import { shippingToBillingFields } from "./constants";

extend(utc);
extend(timezone);

// Utility function to convert empty strings to null in IOrderPayment
function emptyStringToNull<T>(value: T): T | null {
  return value === "" ? null : value;
}

export const defaultPayment = (): IOrderPayment => ({
  id: 0,
  orderKey: "",
  orderNumber: "",
  clientOrderId: "",
  paymentStatus: PaymentStatus.Approved,
  issueDate: getISOCurrentDateTime(),
  installmentNo: "",
  dueDate: getISOCurrentDateTime(),
  paymentDate: getISOCurrentDateTime(),
  paymentMethodType: PaymentMethod.Pix,
  paymentMethodName: "",
  paymentAmount: 0,
  paymentccNumber: null,
  paymentCcname: "",
  paymentCcInstallment: 0,
  paymentCcAuthCode: "",
  paymentCcDocumentNumber: "",
  paymentCctId: "",
  paymentCcNsu: "",
  paymentCcToken: "",
  paymentCcGatewayTran: "",
  slipPaymentDueDate: getISOCurrentDateTime(),
  slipPaymentNumber: "",
  wireTransferBankNumber: "",
  wireTransferBankAgencyNumber: "",
  wireTransferBankAgencyCkDigit: "",
  wireTransferBankAccNumber: "",
  wireTransferBankAccCheckNumber: "",
  rowDate: getISOCurrentDateTime(),
});

export const buildInitialData = (
  customerId: number,
  order: IOrder | null,
  user: IUser | null,
  isEditing: boolean,
  disabledButton: boolean
): OrderFormData => {
  const isAttendantOrRepresentative = user?.userProfile?.some((profile) =>
    [UserProfile.ATTENDANT, UserProfile.REPRESENTATIVE].includes(profile.profileId)
  );

  return {
    // ORDER DATA TAB

    // INFO GROUP
    orderNumber: order ? order.orderNumber : `${customerId}-${Date.now()}`,
    orderDate: order ? order.orderDate : new Date().toISOString(),
    salesChannel: order ? order.salesChannel : OrderSalesChannel.TALK2BUY,
    salesRepresentative:
      order?.salesRepresentative || (isAttendantOrRepresentative ? user?.id : undefined),
    orderStatus: order ? order.orderStatus : OrderStatus.Confirmed,

    // CUSTOMER GROUP
    shippingDocumentType: order ? order.shippingDocumentType : DocumentType.CPF,
    shippingDocument: order ? order.shippingDocument : "",
    shippingName: order ? order.shippingName : "",
    shippingGender: order ? order.shippingGender : "",
    shippingPhoneNumber: order ? order.shippingPhoneNumber : "",
    shippingEmail: order ? order.shippingEmail : "",

    // DELIVERY GROUP
    shippingAddressZipcode: order ? order.shippingAddressZipcode : "",
    shippingAddress: order ? order.shippingAddress : "",
    shippingAddressNumber: order ? order.shippingAddressNumber : "",
    shippingAddressCompl: order ? order.shippingAddressCompl : "",
    shippingAddressDistrict: order ? order.shippingAddressDistrict : "",
    shippingAddressCity: order ? order.shippingAddressCity : "",
    shippingAddressState: order ? order.shippingAddressState : "",
    shippingAddressCountry: order ? order.shippingAddressCountry : "BRASIL",

    // ------------------------------------ //

    // ITEMS TAB
    items: order && order?.items && order?.items.length ? order.items : [],

    // ------------------------------------ //

    // PAYMENTS TAB
    payments:
      order && order?.payments && order?.payments.length ? order.payments : [defaultPayment()],

    orderAmount: order ? order.orderAmount : 0,
    orderFreight: order ? order.orderFreight : 0,
    orderDiscount: order ? order.orderDiscount : 0,
    orderCharges: order ? order.orderCharges : 0,
    orderFee: order ? order.orderFee : 0,

    // ------------------------------------ //

    // Billing Data
    billingPersonType: order ? order.billingPersonType : PersonType.Individual,
    billingAddress: order ? order.billingAddress : "",
    billingAddressNumber: order ? order.billingAddressNumber : "",
    billingAddressCompl: order ? order.billingAddressCompl : "",
    billingAddressDistrict: order ? order.billingAddressDistrict : "",
    billingAddressCity: order ? order.billingAddressCity : "",
    billingAddressCityCode: order ? order.billingAddressCityCode : "",
    billingAddressState: order ? order.billingAddressState : "",
    billingAddressStateUf: order ? order.billingAddressStateUf : "",
    billingAddressZipcode: order ? order.billingAddressZipcode : "",
    billingAddressCountry: order ? order.billingAddressCountry : "",
    billingDocumentType: order ? order.billingDocumentType : DocumentType.CPF,
    billingDocument: order ? order.billingDocument : "",
    billingName: order ? order.billingName : "",
    billingGender: order ? order.billingGender : "M",
    billingPhoneNumber: order ? order.billingPhoneNumber : "",
    billingEmail: order ? order.billingEmail : "",
    billingAddressType: order ? order.billingAddressType : "",
    billingAddressOverseas: order ? order.billingAddressOverseas : false,
    billingStateTaxNumber: order ? order.billingStateTaxNumber : "",
    billingCityTaxNumber: order ? order.billingCityTaxNumber : "",
    billingAddressReference: order ? order.billingAddressReference : "",

    // Delivery Data
    shippingVolumes: order ? order.shippingVolumes : 0,
    shippingVolumeType: order ? order.shippingVolumeType : "",
    shippingAgent: order ? order.shippingAgent : "",
    shippingAgentService: order ? order.shippingAgentService : "",
    shippingObject: order ? order.shippingObject : "",
    shippingTrackingUrl: order ? order.shippingTrackingUrl ?? "" : "",
    shippingDate: order ? order.shippingDate : "",
    deliveryDate: order ? order.deliveryDate : null,
    shippingId: order ? order.shippingId : "",
    shippingAddressCityCode: order ? order.shippingAddressCityCode : "",
    shippingAddressStateUf: order ? order.shippingAddressStateUf : "",
    shippingPersonType: order ? order.shippingPersonType : PersonType.Individual,
    shippingStateTaxNumber: order ? order.shippingStateTaxNumber : "",
    shippingCityTaxNumber: order ? order.shippingCityTaxNumber : "",
    shippingAddressReference: order ? order.shippingAddressReference : "",
    shippingAddressType: order ? order.shippingAddressType : "",
    shippingAddressOverseas: order ? order.shippingAddressOverseas : false,
    shippingAgentTaxId: order ? order.shippingAgentTaxId : "",

    // Invoice Data
    invoiceDate: order ? order.invoiceDate : null,
    invoiceNumber: order ? order.invoiceNumber : "",
    invoiceSeries: order ? order.invoiceSeries : "",
    invoiceKey: order ? order.invoiceKey : "",
    invoiceXml: order ? order.invoiceXml : "",

    // Order Comments
    orderComments1: order ? order.orderComments1 : "",
    orderComments2: order ? order.orderComments2 : "",
    orderComments3: order ? order.orderComments3 : "",

    // Payment Data
    paymentTerms: order ? order.paymentTerms : "",
    orderFreightSeller: order ? order.orderFreightSeller : 0,
    discountCouponAmount: order ? order.discountCouponAmount : 0,
    discountCouponCode: order ? order.discountCouponCode : "",
    orderGroup: order ? order.orderGroup : "",
    giftPackingPrice: order ? order.giftPackingPrice : 0,
    receivableInCash: order ? order.receivableInCash : 0,
    changeToGive: order ? order.changeToGive : 0,

    // Customer Data
    customerPlatformId: order ? order.customerPlatformId : null,
    clientOrderId: order ? order.clientOrderId : "",
    companyId: order ? order.companyId : "",
    businessunitId: order ? order.businessunitId : "",

    // Other Data
    orderPaid: order ? order.orderPaid : false,
    grossWeigth: order ? order.grossWeigth : 0,
    netWeigth: order ? order.netWeigth : 0,
    cancelationDate: order ? order.cancelationDate : "",
    packId: order ? order.packId : "",
    fulfillment: order ? order.fulfillment : false,
    buyerId: order ? order.buyerId : "",
    sellerId: order ? order.sellerId : "",
    promisedDeliveryDate: order ? order.promisedDeliveryDate : "",
    invoicingOnSalesChannel: order ? order.invoicingOnSalesChannel : false,
    fraudRisk: order ? order.fraudRisk : false,
    salesSubChannel: order ? order.salesSubChannel : "",
    totalAmount: order ? order.totalAmount : 0,
    conversationId: order ? order.conversationId : null,
    campaignId: order ? order.campaignId : null,
    contactId: order ? order.contactId : null,

    //ORDER FORM DATA FIEDS
    removedPayments: [],
    removedItems: [],

    isEditing: isEditing,
    disabledButton: disabledButton,

    differenceValue: 0,
  };
};

export const buildFormSchema = (
  messages: OrderFormPageMessages,
  activeTab: OrderTabs,
  isEditing = false
) => {
  // Schemas individuais de cada tab
  const orderDataTabSchema = yup.object().shape({
    shippingDocument: yup.string().required(messages.errorRequiredShippingDocument),
    shippingName: yup.string().required(messages.errorRequiredShippingName),
    shippingPhoneNumber: yup
      .string()
      .test("is-valid-phone", messages.errorInvalidPhone, (value) => {
        const digits = (value || "").replace(/\D/g, "");
        if (!digits) return false;

        // Se o usuário ainda está digitando (menos de 10 dígitos), não validar ainda
        if (digits.length < 10) return true;

        // Garante o formato internacional para validação (+<DDI><DDD><número>)
        const phone = parsePhoneNumberFromString(`+${digits}`);
        return Boolean(phone?.isValid());
      })
      .required(messages.errorRequiredPhone),
    shippingAddressZipcode: yup.string().required(messages.errorRequiredZipCode),
    shippingAddress: yup.string().required(messages.errorRequiredAddress),
    shippingAddressNumber: yup.string().required(messages.errorRequiredAddressNumber),
    shippingAddressDistrict: yup.string().required(messages.errorRequiredDistrict),
    shippingAddressCity: yup.string().required(messages.errorRequiredCity),
    shippingAddressState: yup.string().required(messages.errorRequiredState),
    shippingAddressCountry: yup.string().required(messages.errorRequiredCountry),
  });

  const itemDataTabSchema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        quantity: yup
          .number()
          .required(messages.quantityRequired)
          .moreThan(0, messages.quantityMoreThanZero)
          .notOneOf([0], messages.quantityMoreThanZero),
        netUnitPrice: yup
          .number()
          .required(messages.netUnitPriceRequired)
          .moreThan(0, messages.netUnitPriceMoreThanZero)
          .notOneOf([0], messages.netUnitPriceMoreThanZero),
        netLineAmount: yup
          .number()
          .required(messages.netLineAmountRequired)
          .moreThan(0, messages.netLineAmountMoreThanZero)
          .notOneOf([0], messages.netLineAmountMoreThanZero),
        userComment: yup.string().when('needComment', {
          is: true,
          then: (schema) => schema.required(messages.userCommentRequired),
          otherwise: (schema) => schema.nullable(),
        }),
      })
    ),
  });

  // Em edição não revalida cadastro completo: pedido de WhatsApp/orçamento
  // pode ser salvo sem CPF e endereço, como já ocorre na aba de itens.
  switch (activeTab) {
    case "orderDataTab":
      return isEditing ? yup.object().shape({}) : orderDataTabSchema;
    case "itemDataTab":
      return itemDataTabSchema;
    default:
      // Se nenhuma tab for ativa, retornar um schema vazio
      return yup.object().shape({});
  }
};

export const normalizeItems = (items: any[], order: IOrderInput): IOrderItem[] => {
  if (!Array.isArray(items)) return [];

  return items.map(
    (item): IOrderItem => ({
      ...item,
      orderNumber: order.orderNumber ?? null,
      orderKey: order.orderKey ?? null,
      orderReferenceId: item.orderReferenceId ?? null,
      companyId: item.companyId ?? null,
      businessunitId: item.businessunitId ?? null,
      lineType: item.lineType ?? null,
      parentLineNumber: item.parentLineNumber ?? null,
      lineNumber: item.lineNumber ?? null,
      skuID: item.skuID ?? null,
      skuCode: item.skuCode ?? "",
      plaftormSkuCode: item.plaftormSkuCode ?? "",
      externalReference: item.externalReference ?? "",
      kit: item.kit ?? false,
      quantity: item.quantity ?? 0,
      reservedQuantity: item.reservedQuantity ?? 0,
      shippedQuantity: item.shippedQuantity ?? null,
      listPrice: convertCurrencyToNumber(item.listPrice),
      grossUnitPrice: convertCurrencyToNumber(item.grossUnitPrice),
      netUnitPrice: convertCurrencyToNumber(item.netUnitPrice),
      netLineAmount: convertCurrencyToNumber(item.netLineAmount),
      grossLineAmount: convertCurrencyToNumber(item.grossLineAmount),
      giftPacking: item.giftPacking ?? null,
      giftMessage: item.giftMessage ?? null,
      freeOfCharge: item.freeOfCharge ?? null,
      grossWeigth: item.grossWeigth ?? 0,
      netWeigth: item.netWeigth ?? 0,
      description: item.description ?? "",
      variations: item.variations ?? "",
      campaignId: item.campaignId ?? null,
      discountCouponCode: item.discountCouponCode ?? null,
      variationCode01: item.variationCode01 ?? "",
      variationValue01: item.variationValue01 ?? "",
      variationCode02: item.variationCode02 ?? "",
      variationValue02: item.variationValue02 ?? "",
      variationCode03: item.variationCode03 ?? "",
      variationValue03: item.variationValue03 ?? "",
      variationCode04: item.variationCode04 ?? "",
      variationValue04: item.variationValue04 ?? "",
      variationCode05: item.variationCode05 ?? "",
      variationValue05: item.variationValue05 ?? "",
      addInformation: item.addInformation ?? null,
      lineFreight: item.lineFreight ?? null,
      lineFreightSeller: item.lineFreightSeller ?? null,
      uuid: item.uuid ?? undefined,
    })
  );
};

export const normalizePayments = (payments: any[], order: IOrderInput) => {
  if (!Array.isArray(payments)) return [];

  return payments
    .map((payment) => ({
      ...payment,
      customerId: order.customerId,
      orderKey: order.orderKey,
      orderNumber: order.orderNumber,
      paymentMethodName: payment.paymentMethodType,
      paymentAmount: payment.paymentAmount,
    }))
    .map(normalizeEmptyStrings);
};

export const normalizeUpdate = (
  formData: OrderFormData,
  order: IOrderInput | IOrder
): OrderFormData => {
  const billingInfo = Object.entries(shippingToBillingFields).reduce<Partial<OrderFormData>>(
    (acc, [shippingKey, billingKey]) => {
      if (shippingKey in formData) {
        const key = billingKey as keyof OrderFormData;
        acc[key] = formData[shippingKey as keyof OrderFormData];
      }
      return acc;
    },
    {}
  );

  const normalizedFormData: OrderFormData = {
    ...formData,
    ...billingInfo,
    payments: normalizePayments(formData.payments as IOrderPayment[], order),
  };

  return normalizedFormData;
};

export const normalizeCreation = (formData: OrderFormData): IOrderInput => {
  const billingInfo = Object.entries(shippingToBillingFields).reduce<Partial<OrderFormData>>(
    (acc, [shippingKey, billingKey]) => {
      if (shippingKey in formData) {
        const key = billingKey as keyof OrderFormData;
        acc[key] = formData[shippingKey as keyof OrderFormData];
      }
      return acc;
    },
    {}
  );

  const orderAmount = Number(formData.orderAmount || 0);
  const orderFreight = Number(formData.orderFreight || 0);
  const orderCharges = Number(formData.orderCharges || 0);
  const orderDiscount = Number(formData.orderDiscount || 0);
  const expectedPayment = parseFloat(
    (orderAmount + orderFreight + orderCharges - orderDiscount).toFixed(2)
  );

  const firstPayment = Array.isArray(formData.payments) ? formData.payments[0] : undefined;
  const paymentMethodName =
    firstPayment?.paymentMethodName || firstPayment?.paymentMethodType || "";
  const paymentTypeId = (firstPayment as any)?.paymentTypeId ?? null;

  const items = Array.isArray(formData.items)
    ? formData.items.map((i) => {
        const normalizedItem = {
          skuCode: i.skuCode,
          quantity: Number(i.quantity),
          netUnitPrice: Number(i.netUnitPrice),
          description: i.description ?? "",
          userComment: i.needComment ? (i.userComment?.trim() || "") : "",
        };
        
        // 🔍 LOG TEMPORÁRIO PARA DEBUG
        console.log('🔍 Item antes de enviar:', {
          skuCode: i.skuCode,
          needComment: i.needComment,
          userCommentOriginal: i.userComment,
          userCommentOriginalType: typeof i.userComment,
          userCommentFinal: normalizedItem.userComment,
          userCommentFinalType: typeof normalizedItem.userComment,
        });
        
        return normalizedItem;
      })
    : [];

  const isInternal = !formData.customerPlatformId || formData.customerPlatformId === 0;

  const normalized: Partial<IOrderInput> = {
    customerPlatformId: isInternal ? null : formData.customerPlatformId,
    orderNumber: null as unknown as string,
    clientOrderId: null as unknown as string,
    orderDate: dayjs().tz("America/Sao_Paulo").format(),
    // Na criação, exibir e enviar C (Confirmado) como "Aguardando pagamento" na UI
    orderStatus: OrderStatus.Confirmed,
    salesChannel: formData.salesChannel || OrderSalesChannel.TALK2BUY,

    orderAmount,
    orderFreight,
    orderCharges,
    orderDiscount,

    billingName: (billingInfo as any).billingName ?? formData.billingName,
    billingGender: (billingInfo as any).billingGender ?? formData.billingGender,
    billingPersonType: (billingInfo as any).billingPersonType ?? formData.billingPersonType,
    billingEmail: (billingInfo as any).billingEmail ?? formData.billingEmail,
    billingPhoneNumber:
      (billingInfo as any).billingPhoneNumber ?? (formData.billingPhoneNumber as string),
    billingAddress: (billingInfo as any).billingAddress ?? formData.billingAddress,
    billingAddressNumber:
      (billingInfo as any).billingAddressNumber ?? formData.billingAddressNumber,
    billingAddressDistrict:
      (billingInfo as any).billingAddressDistrict ?? formData.billingAddressDistrict,
    billingAddressCity: (billingInfo as any).billingAddressCity ?? formData.billingAddressCity,
    billingAddressState: (billingInfo as any).billingAddressState ?? formData.billingAddressState,
    billingAddressZipcode:
      (billingInfo as any).billingAddressZipcode ?? formData.billingAddressZipcode,
    billingAddressCountry:
      (billingInfo as any).billingAddressCountry ?? formData.billingAddressCountry,
    billingDocument: (billingInfo as any).billingDocument ?? formData.billingDocument,
    billingDocumentType: (billingInfo as any).billingDocumentType ?? formData.billingDocumentType,

    shippingName: formData.shippingName,
    shippingGender: formData.shippingGender,
    shippingPersonType: formData.shippingPersonType,
    shippingEmail: formData.shippingEmail,
    shippingPhoneNumber: formData.shippingPhoneNumber,
    shippingAddress: formData.shippingAddress,
    shippingAddressNumber: formData.shippingAddressNumber,
    shippingAddressDistrict: formData.shippingAddressDistrict,
    shippingAddressCity: formData.shippingAddressCity,
    shippingAddressState: formData.shippingAddressState,
    shippingAddressZipcode: formData.shippingAddressZipcode,
    shippingAddressCountry: formData.shippingAddressCountry,
    shippingDocument: formData.shippingDocument,
    shippingDocumentType: formData.shippingDocumentType,

    items: items as unknown as IOrderItem[],
    payments: [
      {
        paymentStatus: PaymentStatus.Approved,
        paymentMethodName,
        paymentAmount: expectedPayment,
        paymentTypeId,
      } as unknown as IOrderPayment,
    ],
  };

  return normalized as IOrderInput;
};
